import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import Button from "../components/Button";
import { CUSTOMER_PORTAL_URL } from "../lib/portal";
import PageHero from "../components/PageHero";
import { posts } from "../data/siteContent";
import NotFound from "./NotFound";
import { apiRequest } from "../lib/api";

export function BlogPost() {
  const { slug } = useParams();
  const [remotePost, setRemotePost] = useState(null);
  const [remoteLoading, setRemoteLoading] = useState(true);
  useEffect(() => { let active = true; setRemoteLoading(true); apiRequest(`/public/blog/${slug}`).then((response) => { if (active) setRemotePost(response.data); }).catch(() => {}).finally(() => { if (active) setRemoteLoading(false); }); return () => { active = false; }; }, [slug]);
  const post = remotePost || posts.find((item) => item.slug === slug);
  if (!post && remoteLoading) return <PageHero eyebrow="Insights" title="Loading article…" copy="" />;
  if (!post) return <NotFound />;
  return (
    <>
      <PageHero eyebrow={post.category} title={post.title} copy={post.excerpt}>
        <p className="mt-5 text-xs font-bold uppercase tracking-[.15em] text-slate-400">
          {post.date} · {post.readTime}
        </p>
      </PageHero>
      <article id="content" className="section-space bg-white">
        <div className="container grid gap-12 lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-20">
          <div className="mx-auto w-full max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-bold text-ocean transition hover:text-ink"
            >
              <ArrowLeft size={16} />
              Back to journal
            </Link>

            {remotePost?.body ? <div className="prose prose-slate mt-9" dangerouslySetInnerHTML={{ __html: remotePost.body }} /> : <div className="prose prose-slate mt-9">
              <p className="lead">
                The Bridge exists for the moment when a good intention needs a
                reliable operating rhythm behind it. That means making the brief
                clear, giving the work an owner and keeping the customer close to
                the decisions that matter.
              </p>
              <h2 id="next-step">Make the next step visible</h2>
              <p>
                Every service has a different set of details, but the underlying
                experience should feel consistent. We start with your needs, map
                the dependencies and turn the work into milestones that can be
                understood at a glance.
              </p>
              <h2 id="evidence">Build trust through evidence</h2>
              <p>
                Updates are useful when they answer three questions: what happened,
                what happens next and what do we need from you? That is why
                documents, messages and completion evidence belong with the
                order—not scattered across channels.
              </p>
              <h2 id="relationship">Execution is a relationship</h2>
              <p>
                Technology helps us coordinate, but thoughtful human ownership is
                what closes the gap between a request and a result. That is the
                standard we are building towards as The Bridge grows.
              </p>
              <div className="not-prose mt-12 rounded-3xl bg-mist p-7 sm:p-8">
                <p className="font-display text-xl font-bold text-ink">
                  Have something you need to move?
                </p>
                <p className="mt-2 text-sm leading-7 text-slate">
                  Bring us the context and we will help shape the next step.
                </p>
                <Button href={`${CUSTOMER_PORTAL_URL}/signup`} className="mt-5">
                  Get started
                </Button>
              </div>
            </div>}
          </div>

          <aside className="hidden self-start border-l border-ink/10 pl-6 lg:sticky lg:top-28 lg:block">
            <p className="eyebrow">In this article</p>
            <nav className="mt-5 space-y-3 text-sm leading-6 text-slate" aria-label="Article sections">
              <a href="#next-step" className="block transition hover:text-ocean">
                Make the next step visible
              </a>
              <a href="#evidence" className="block transition hover:text-ocean">
                Build trust through evidence
              </a>
              <a href="#relationship" className="block transition hover:text-ocean">
                Execution is a relationship
              </a>
            </nav>
          </aside>
        </div>
      </article>
    </>
  );
}
